/**
 * Vehicle-Mode day-view source qualification (VU-absence design item 2,
 * 2026-08-18).
 *
 * WHY THIS MODULE EXISTS, and why the unqualified heading was a defect:
 * the Vehicle-Mode day view headed EVERY day's records table "Vehicle Unit
 * Activity Log" and captioned its timeline "VEHICLE UNIT / CARD DATA",
 * whether or not a single vehicle-unit row backed the day. Prod: R21ERL had
 * ZERO VU rows after 2026-06-06 (9,752 card rows), yet its August day views
 * read as a vehicle-unit log complete with odometer readings — a transport
 * manager reading that concludes the VU was downloaded. DDIR asked exactly
 * that question, and the honest answer (all card-sourced) made the heading
 * TP's defect. Note the odometer values are GENUINE — driver cards carry
 * odometer at insertion/withdrawal — so nothing is hidden here; the day is
 * re-LABELLED, never redacted.
 *
 * The source cannot be derived from the day's rows: canonical activities
 * are re-inserted under per-driver 'merged::<driver>' source_files, which
 * destroys each row's card/VU origin. So the component asks the backend
 * (/api/vehicle-vu-coverage — same file_metadata coverage windows as the
 * calendar's vehiclesWithoutVu, shared helper server-side) and this module
 * turns the answer into the three rendered strings.
 *
 * THE DEGRADE RULE (do not "simplify" it away): qualification requires a
 * POSITIVE "not covered" (vuCovered === false). A failed fetch, an error
 * body, a proxy's HTML page — all normalise to null ("unknown") and render
 * the day UNQUALIFIED, exactly as before the fix. Branding a healthy VU day
 * "card data only" would be the same sin in the opposite direction.
 *
 * Governing principle (design doc): never render the absence of evidence as
 * the presence of compliance — and never render a lookup failure as the
 * absence of evidence. Pinned by `vehicleDaySource.test.mjs` (`node --test`).
 */

/**
 * A registration as a comparison key: a VU spells "PF23 BBO" where the
 * roster spells "PF23BBO", and the two must be ONE truck.
 *
 * It lives here because three surfaces need it and each had its own answer:
 * lib/calendarCell carried a local copy (the month ring's self-exclusion),
 * lib/calendarStatus had none at all (so the week badge and the day bar
 * chased the very truck the month ring excused), and the day view's Dual
 * panel compared nothing. One key, one module — the lowest one, which
 * imports nothing.
 *
 * Not lib/people's `normVrn`, which is the same three lines: importing it
 * drags lib/actionQueueItems and with it lucide-react behind `node --test`.
 *
 * @param {*} r a registration in any spelling
 * @returns {string} '' on nothing
 */
export function vrnKey(r) {
  return String(r || '').replace(/\s+/g, '').toUpperCase();
}

/* The API's mask for a registration outside this organisation (server-side
   `_mask_external_vehicles`). It arrives in the activity rows where a plate
   would be, and it is NOT one: it names no truck and matches no vehicle to
   fetch. Held as a key so the comparison is the same one every registration
   gets here. */
const EXTERNAL_VEHICLE_KEY = vrnKey('External Vehicle');

/**
 * Normalise the /api/vehicle-vu-coverage payload to the tri-state signal.
 * @param {*} payload response body (may be garbage on a proxy/error path)
 * @returns {boolean|null} true = VU-backed, false = definitively no VU
 *   download covers the day, null = unknown (render unqualified).
 */
export function normalizeVuCoverage(payload) {
  // STRICT booleans only: `vuCovered: 1`, `'no'`, a missing key, an error
  // object — none is a positive claim in either direction.
  const v = payload && typeof payload === 'object' ? payload.vuCovered : undefined;
  return typeof v === 'boolean' ? v : null;
}

/**
 * The ANALOGUE CHART (2026-09-18) strictness rule — THE one copy.
 *
 * A third source arrived after the two above: a photographed paper disc,
 * read in and turned into activity rows like any other day. The same
 * endpoint now answers `tachographType` and an `analogueChart` object, and
 * reports `vuCovered: true` when a chart covers the day — so the card-only
 * branch is already quiet on a chart day. The heading still has to change:
 * "Vehicle Unit Activity Log" over a paper disc is the same class of defect
 * this module exists to prevent, one source-name further out.
 *
 * Same strictness doctrine as normalizeVuCoverage: a plausible chart is an
 * OBJECT carrying a non-empty string `chart_id`. An error body, a proxy's
 * HTML page, a bare id, an array, a chart with no id — every one of those is
 * null, and the day falls back to the ordinary two states. Re-labelling a
 * digital day "analogue chart" off a malformed payload would be the same sin
 * as branding a healthy VU day "card data only".
 *
 * lib/calendarStatus (the calendar's day state) and lib/pattern (the wall
 * chart) IMPORT this. They each carried their own copy behind a comment
 * saying the lib modules have no cross-imports — which was untrue
 * (calendarCell imports calendarStatus; pattern imports today and people),
 * and three copies of the rule that stops a malformed payload re-labelling a
 * day are three chances for the surfaces to disagree about what a chart is.
 *
 * @param {*} c a candidate chart object
 * @returns {object|null}
 */
export function plausibleChart(c) {
  if (!c || typeof c !== 'object' || Array.isArray(c)) return null;
  return typeof c.chart_id === 'string' && c.chart_id ? c : null;
}

/**
 * The day's chart off a /api/vehicle-vu-coverage payload, or null — the rule
 * above applied one level in. A payload that is not an object (an error body,
 * a proxy's HTML page, an array) has no `analogueChart` to read, so it lands
 * on the same null.
 *
 * @param {*} payload response body from /api/vehicle-vu-coverage
 * @returns {object|null}
 */
export function analogueChartFromCoverage(payload) {
  return plausibleChart(payload?.analogueChart);
}

/**
 * The MISSING-DISC flag (plan 2026-10-06 Task 1.3, "honest days") — the
 * calendar day's and /api/vehicle-vu-coverage's `analogueMissing`:
 * `{explained, partial, vrns, whole_vrns, gap_keys, gap_km?, problem_kind?}`
 * or null. Same strictness as plausibleChart, and here for the same reason:
 * the lowest module, so the calendar's day state and the day view read one
 * rule. A plausible flag is an OBJECT whose `explained` is a real boolean
 * (the server always sends it); anything else is null, and the day renders
 * exactly as it did before the flag existed. Its lists come back as arrays
 * of strings, whatever arrived, so a reader never has to guard them again.
 *
 * @param {*} m a candidate flag
 * @returns {object|null}
 */
export function plausibleMissing(m) {
  if (!m || typeof m !== 'object' || Array.isArray(m)) return null;
  if (typeof m.explained !== 'boolean') return null;
  const list = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x.trim()) : []);
  const out = {
    ...m,
    partial: m.partial === true,
    vrns: list(m.vrns),
    whole_vrns: list(m.whole_vrns),
    gap_keys: list(m.gap_keys),
  };
  /* The trucks still red (review I1). Absent from an older server: then the
     day-level `explained` is all there is, and the key stays absent so a
     reader can tell "none" from "not said". */
  if (Object.prototype.hasOwnProperty.call(m, 'unexplained_whole_vrns')) {
    out.unexplained_whole_vrns = list(m.unexplained_whole_vrns);
  }
  /* The trucks whose partial mark is still open, so a truck's own view
     speaks only for its own mark. Kept absent from an older server too. */
  if (Object.prototype.hasOwnProperty.call(m, 'unexplained_partial_vrns')) {
    out.unexplained_partial_vrns = list(m.unexplained_partial_vrns);
  }
  return out;
}

/**
 * The day's missing-disc flag off a /api/vehicle-vu-coverage payload, or
 * null: the rule above one level in, as analogueChartFromCoverage.
 *
 * @param {*} payload response body from /api/vehicle-vu-coverage
 * @returns {object|null}
 */
export function analogueMissingFromCoverage(payload) {
  return plausibleMissing(payload?.analogueMissing);
}

/**
 * Should the vehicle-mode day view brand itself "card data only"?
 * @param {{vuCovered: (boolean|null|undefined), hasRows: boolean}} args
 *   vuCovered — normalised tri-state from normalizeVuCoverage.
 *   hasRows — whether the day renders any records at all (activities OR
 *     unaccounted OR out-of-scope bands). An empty day stays unqualified:
 *     there are no rows to mislabel, and the empty-state copy already says
 *     "No card activity recorded".
 * @returns {boolean}
 */
export function isCardOnlyVehicleDay({ vuCovered, hasRows }) {
  return vuCovered === false && hasRows === true;
}

/**
 * The records-table heading. When the day is NOT card-only this must return
 * the exact pre-fix string — the VU-backed rendering is contractually
 * byte-identical to before this module existed.
 * @param {boolean} cardOnly
 * @returns {string}
 */
export function vehicleDayLogHeading(cardOnly) {
  return cardOnly ? 'Activity Log — card data only' : 'Vehicle Unit Activity Log';
}

/**
 * The records-table heading in all THREE states, and the entry point the
 * component calls. A chart day wins outright — belt-and-braces over the
 * backend's `vuCovered: true`, so a coverage/chart disagreement can never
 * brand a paper day "card data only".
 *
 * The degrade rule is untouched underneath: with no chart, an unknown
 * coverage (null) or an empty day still renders the exact pre-fix string.
 *
 * `plate` is the display registration ('PF23 BBO'); with none, the chart's
 * own `vrn` is used, and with neither the bare words — never the VU heading.
 * The module stays dependency-free (no plateDisplay import) so it can be
 * unit-tested and reasoned about on its own.
 *
 * @param {{vuCovered: (boolean|null|undefined), hasRows: boolean,
 *          analogueChart: (object|null|undefined), plate: (string|undefined)}} args
 * @returns {string}
 */
export function vehicleDayHeading({ vuCovered, hasRows, analogueChart, plate } = {}) {
  const chart = plausibleChart(analogueChart);
  if (chart) {
    const reg = (typeof plate === 'string' && plate.trim())
      || (typeof chart.vrn === 'string' && chart.vrn.trim())
      || '';
    return reg ? `Analogue chart · ${reg}` : 'Analogue chart';
  }
  return vehicleDayLogHeading(isCardOnlyVehicleDay({ vuCovered, hasRows }));
}

/**
 * The one-line qualifier under the card-only heading; null when VU-backed
 * (nothing extra renders). States BOTH halves of the truth: where the rows
 * came from, and that the odometer readings are real (from the cards) —
 * qualified, not hidden.
 * @param {boolean} cardOnly
 * @returns {string|null}
 */
export function vehicleDayLogQualifier(cardOnly) {
  return cardOnly
    ? 'No vehicle-unit download covers this day. Rows and odometer readings are from driver cards.'
    : null;
}

/**
 * The timeline caption (rendered upper-case by the Timeline component). On
 * a card-only day the "Vehicle Unit /" half is simply false and is dropped;
 * on a CHART day both halves are false — there is neither a unit download
 * nor a driver card behind the rows, only a paper disc — so the caption
 * names the disc. On a VU-backed day this must return the exact pre-fix
 * string.
 * @param {boolean} cardOnly
 * @param {string} vehicle registration for the caption suffix
 * @param {object|null} [analogueChart] the day's chart, when it has one
 * @returns {string}
 */
export function vehicleDayTimelineLabel(cardOnly, vehicle, analogueChart = null) {
  if (plausibleChart(analogueChart)) return `Analogue Chart — ${vehicle}`;
  return cardOnly
    ? `Card Data — ${vehicle}`
    : `Vehicle Unit / Card Data — ${vehicle}`;
}

/**
 * The sentence in the Dual View's empty second panel — the driver-mode day
 * view's vehicle-unit column when the fetch came back with no rows.
 *
 * A CHART EXPLAINS ONE TRUCK, AND ONLY THE TRUCK IT CAME OFF. The panel
 * fetches a SINGLE registration (the first on the driver's activities) while
 * the day's chart is a DAY-level fact, so the two need not be about the same
 * vehicle. On the prod shape this feature was written for — AB12CDE charted,
 * R21ERL digital and never downloaded, one driver, one day — gating the chart
 * sentence on the chart alone means that when the activity ordering puts
 * R21ERL first the panel comes back empty BECAUSE R21ERL was never
 * downloaded, and then says "there is no vehicle unit download behind it".
 * That asserts a paper disc accounts for a download that is genuinely owed on
 * another truck: the v3.52.14 defect class (absence of vehicle-side evidence
 * rendered as an account of where the hours came from), and intermittent,
 * because it turns on which activity sorts first.
 *
 * So the chart sentence needs the chart's truck and the fetched truck to be
 * the same one, by `vrnKey` (the calendar's own key — a VU's "R21 ERL" and a
 * roster's "R21ERL" are one truck). Anything unprovable — a chart with no
 * registration, a garbage payload, no truck on the panel at all — falls back
 * to the neutral sentence, which NAMES the truck it found nothing for so the
 * manager knows which download is missing.
 *
 * `plate` is the caller's display spelling ('R21 ERL', lib/people's
 * plateDisplay); with none the raw registration is used, and with neither the
 * exact pre-chart sentence. Same import-free idiom as vehicleDayHeading.
 *
 * 'External Vehicle' is not a registration: it is the API's MASK for a truck
 * outside this organisation (server-side _mask_external_vehicles), and the
 * panel is empty in exactly that case because the mask matches no vehicle to
 * fetch. It names no truck, and its `plate` would come through plateDisplay
 * as "EXTERNALVEHI CLE" — so it is read as no truck at all.
 *
 * `loaded` IS THE POINT OF THIS SIGNATURE (2026-09-18, whole-branch review).
 * Every sentence below describes a lookup that SUCCEEDED and came back with
 * nothing: "there is no vehicle unit download behind it" and "No vehicle unit
 * data for R21 ERL on this day" are both claims about the operator's
 * evidence. The Dual View's fetch had only two render states — loading, and
 * zero rows — so a 500, a proxy's HTML page, a dropped connection or a 403
 * all arrived here as "zero rows" and were published as one of those claims,
 * on a surface a transport manager acts on. That is the governing principle
 * at the top of this file, in its worst shape yet: a lookup failure rendered
 * as the absence of evidence.
 *
 * So the caller must SAY the lookup came back. Anything but a literal `true`
 * — false, undefined, a forgotten argument — returns null, and the caller
 * renders vehicleUnitLoadFailedWords() instead. A convention would have held
 * for today's one caller; the argument is what stops the next one.
 *
 * @param {{loaded: boolean, analogueChart: (object|null|undefined),
 *          vehicleReg: (string|undefined), plate: (string|undefined)}} args
 * @returns {string|null} null when the lookup has not come back
 */
export function vehicleUnitEmptyWords({ loaded, analogueChart, vehicleReg, plate } = {}) {
  if (loaded !== true) return null;
  const given = typeof vehicleReg === 'string' ? vehicleReg.trim() : '';
  const raw = vrnKey(given) === EXTERNAL_VEHICLE_KEY ? '' : given;
  const shown = raw ? ((typeof plate === 'string' && plate.trim()) || raw) : '';
  const chart = plausibleChart(analogueChart);
  const chartKey = chart && typeof chart.vrn === 'string' ? vrnKey(chart.vrn) : '';
  if (chartKey && chartKey === vrnKey(shown)) {
    return 'This day was recorded on an analogue tachograph chart — a paper disc, '
      + 'photographed and read in. There is no vehicle unit download behind it.';
  }
  return shown
    ? `No vehicle unit data for ${shown} on this day.`
    : 'No vehicle unit data available for this day.';
}

/**
 * What the Dual View's vehicle panel says when the lookup FAILED — the third
 * state, and the only honest one when we do not know.
 *
 * It says nothing about the day: not that a chart accounts for it, not that a
 * download is missing, not how many records there are. It reports what
 * actually happened — the records could not be read — in the same voice as
 * the Pattern view's "Couldn't load the pattern. Refresh." Deliberately NOT
 * the raw error message: `err.message` in the evidence column ("Failed to
 * fetch") reads to a manager as a statement about the truck.
 *
 * It names no registration on purpose: the failure is the lookup's, not the
 * vehicle's, and a plate in the sentence invites reading it as a finding
 * about that plate.
 *
 * @returns {string}
 */
export function vehicleUnitLoadFailedWords() {
  return 'Couldn’t load the vehicle unit records for this day.';
}
